import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Box,
  Button,
  TextField,
  Typography,
  MenuItem,
  Alert,
  Container,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
  IconButton,
  Select,
  InputLabel,
  Stack,
  Chip,
  Checkbox,
} from "@mui/material";
import {
  CloudUploadOutlined,
  PersonAddOutlined,
  CheckCircle,
  Cancel,
  ArrowBack,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { useAuth } from "../hooks/useAuth";
import { statusTextPT, uiPT } from "../i18n";
import Card from "../components/Card";
import { cleanWhatsAppNumber, validateWhatsAppNumber } from "../utils/phoneUtils";
import { MuiTelInput } from 'mui-tel-input';

interface Partner {
  _id: string;
  partnerCode: string;
  name: string;
  IBAN: string;
  referredFee: number;
  referralLink: string;
  isActive: boolean;
}

interface FormData {
  name: string;
  type: string;
  modality: string;
  phoneNumber: string;
  IBAN: string;
  identityType: string;
  city: string;
  status: string;
  idCardType: "pdf" | "image";
  isReferred: boolean;
  partnerId?: string;
  selfie?: File;
  modalityPhoto?: File;
  driverLicense?: File;
  idCardFront?: File;
  idCardBack?: File;
  idCardPdf?: File;
  caeDocument?: File;
  taxClearanceDocument?: File;
}

const schema = yup.object({
  name: yup
    .string()
    .required("Nome é obrigatório")
    .min(2, "Nome deve ter pelo menos 2 caracteres"),
  type: yup
    .string()
    .required("Tipo é obrigatório")
    .oneOf(["courier", "driver"], "Tipo inválido"),
  modality: yup
    .string()
    .required("Modalidade é obrigatória")
    .oneOf(["bike", "motorcycle"], "Modalidade inválida"),
  phoneNumber: yup.string().when("isReferred", {
    is: false,
    then: (schema) => schema
      .required("Telefone WhatsApp é obrigatório")
      .test('whatsapp-validation', 'Número WhatsApp deve ser um número internacional válido (ex: +351 912 345 678, +1 555 123 4567)', (value) => validateWhatsAppNumber(value || '')),
    otherwise: (schema) => schema.optional(),
  }),
  IBAN: yup
    .string()
    .required("IBAN é obrigatório")
    .transform((value) => (value ? value.replace(/\s/g, "") : value))
    .matches(
      /^[A-Z]{2}[0-9]{2}[A-Z0-9]{4}[0-9]{7}([A-Z0-9]?){0,16}$/,
      "IBAN inválido"
    ),
  identityType: yup
    .string()
    .required("Tipo de identidade é obrigatório")
    .oneOf(["passport", "cc", "trc", "cplp"], "Tipo de identidade inválido"),
  city: yup
    .string()
    .required("Cidade é obrigatória")
    .min(2, "Cidade deve ter pelo menos 2 caracteres"),
  status: yup.string().required("Status é obrigatório"),
  idCardType: yup
    .string()
    .required("Tipo de documento é obrigatório")
    .oneOf(["pdf", "image"], "Tipo inválido"),
  isReferred: yup.boolean().required(),
  partnerId: yup.string().when("isReferred", {
    is: true,
    then: (schema) => schema.required("Parceiro é obrigatório para clientes indicados"),
    otherwise: (schema) => schema.optional(),
  }),
  selfie: yup.mixed().required("Selfie é obrigatória"),
  modalityPhoto: yup.mixed().required("Foto da mochila é obrigatória"),
  driverLicense: yup.mixed().when("modality", {
    is: "motorcycle",
    then: (schema) => schema.required("Carta de condução é obrigatória para motocicleta"),
    otherwise: (schema) => schema.optional(),
  }),
  idCardFront: yup.mixed().when("idCardType", {
    is: "image",
    then: (schema) => schema.required("Frente do CC/TR/CPLP é obrigatória"),
    otherwise: (schema) => schema.optional(),
  }),
  idCardBack: yup.mixed().when("idCardType", {
    is: "image",
    then: (schema) => schema.required("Verso do CC/TR/CPLP é obrigatório"),
    otherwise: (schema) => schema.optional(),
  }),
  idCardPdf: yup.mixed().when("idCardType", {
    is: "pdf",
    then: (schema) => schema.required("CC/TR/CPLP em PDF é obrigatório"),
    otherwise: (schema) => schema.optional(),
  }),
  caeDocument: yup.mixed().required('Código de atividade económica (CAE) é obrigatório'),
  taxClearanceDocument: yup.mixed().required('Certidão de Não Dívida - Autoridade Tributária é obrigatória'),
}) as yup.ObjectSchema<FormData>;

export default function ClientCreatePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [trackingCode, setTrackingCode] = useState<string | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<Record<string, string>>(
    {}
  );
  const [partners, setPartners] = useState<Partner[]>([]);
  const [partnersLoading, setPartnersLoading] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: "",
      type: "",
      modality: "",
      phoneNumber: "",
      IBAN: "",
      identityType: "",
      city: "",
      status: "CRIAR",
      idCardType: "image",
      isReferred: false,
      partnerId: "",
    },
  });

  const modality = watch("modality");
  const idCardType = watch("idCardType");
  const isReferred = watch("isReferred");

  // Fetch partners when component mounts
  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    setPartnersLoading(true);
    try {
      const response = await api.get("/partners");
      // Filter only active partners
      const activePartners = response.data.data.filter((partner: Partner) => partner.isActive);
      setPartners(activePartners);
    } catch (err: any) {
      console.error("Error fetching partners:", err);
    } finally {
      setPartnersLoading(false);
    }
  };

  // IBAN mask function
  const formatIBAN = (value: string) => {
    const cleaned = value.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
    const formatted = cleaned.replace(/(.{4})/g, "$1 ").trim();
    return formatted;
  };

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    setTrackingCode(null);

    try {
      const formData = new FormData();

      // Add form fields
      formData.append("name", data.name);
      formData.append("type", data.type);
      formData.append("modality", data.modality);
      formData.append("IBAN", data.IBAN.replace(/\s/g, ""));
      formData.append("identityType", data.identityType);
      formData.append("city", data.city);
      formData.append("status", data.status);
      formData.append('idCardType', data.idCardType);

      // Add phoneNumber only if not referred
      if (!data.isReferred) {
        formData.append("phoneNumber", cleanWhatsAppNumber(data.phoneNumber));
      }

      // Add partnerId if referred
      if (data.isReferred && data.partnerId) {
        formData.append("partnerId", data.partnerId);
      }

      // Add file fields
      if (data.selfie) formData.append("selfie", data.selfie);
      if (data.modalityPhoto)
        formData.append("modalityPhoto", data.modalityPhoto);
      if (data.driverLicense)
        formData.append("driverLicense", data.driverLicense);
      if (data.idCardFront) formData.append("idCardFront", data.idCardFront);
      if (data.idCardBack) formData.append("idCardBack", data.idCardBack);
      if (data.idCardPdf) formData.append("idCardPdf", data.idCardPdf);
      if (data.caeDocument) formData.append("caeDocument", data.caeDocument);
      if (data.taxClearanceDocument) formData.append("taxClearanceDocument", data.taxClearanceDocument);

      let endpoint = '/client/register';
      
      // Use different endpoint for referred clients
      if (data.isReferred && data.partnerId) {
        // Find the partner to get their referral link
        const selectedPartner = partners.find(p => p._id === data.partnerId);
        if (selectedPartner) {
          // Extract linkId from referral link (e.g., "https://activoo.app/refer/linkId" -> "linkId")
          const linkId = selectedPartner.referralLink.split('/refer/')[1];
          endpoint = `/referrals/register/${linkId}`;
        }
      }

      const res = await api.post(endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setSuccess("Cliente criado com sucesso!");
      setTrackingCode(res.data.data?.trackingCode || res.data.trackingCode);
    } catch (err: any) {
      setError(err.response?.data?.message || "Erro ao criar cliente");
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange =
    (field: keyof FormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        setValue(field, file);
        setUploadedFiles((prev) => ({
          ...prev,
          [field]: file.name,
        }));
      }
    };

  const removeFile = (field: keyof FormData) => {
    setValue(field, undefined);
    setUploadedFiles((prev) => {
      const newFiles = { ...prev };
      delete newFiles[field];
      return newFiles;
    });
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: (theme) => theme.palette.grey[100],
        py: 3,
      }}
    >
      <Container maxWidth="lg">
        <Stack spacing={3}>
          {/* Header */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton
              onClick={() => navigate(-1)}
              sx={{ mr: 2 }}
            >
              <ArrowBack />
            </IconButton>
            <Typography
              variant="h4"
              sx={{ fontWeight: 800 }}
            >
              Criar Novo Cliente
            </Typography>
          </Box>

          <Card sx={{ p: 4 }}>
            <Stack spacing={4}>
              {/* Icon Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: 2,
                    background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: (theme) => theme.shadows[3],
                  }}
                >
                  <PersonAddOutlined sx={{ color: 'white', fontSize: 32 }} />
                </Box>
              </Box>

              <form onSubmit={handleSubmit(onSubmit)}>
                <Stack spacing={3}>
              {/* Basic Information */}
              <Typography
                variant="h6"
                sx={{ color: "#2c3e50", fontWeight: 600 }}
              >
                Informações Básicas
              </Typography>

              <Stack direction="row" spacing={3} flexWrap="wrap">
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={uiPT.register.name}
                      error={!!errors.name}
                      helperText={errors.name?.message}
                      required
                      sx={{
                        flex: 1,
                        minWidth: 250,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          "&:hover fieldset": {
                            borderColor: "#667eea",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#667eea",
                          },
                        },
                      }}
                    />
                  )}
                />
                <Controller
                  name="type"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      label={uiPT.register.type}
                      error={!!errors.type}
                      helperText={errors.type?.message}
                      required
                      sx={{
                        flex: 1,
                        minWidth: 250,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          "&:hover fieldset": {
                            borderColor: "#667eea",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#667eea",
                          },
                        },
                      }}
                    >
                      <MenuItem value="courier">
                        {uiPT.register.courier}
                      </MenuItem>
                      <MenuItem value="driver">{uiPT.register.driver}</MenuItem>
                    </TextField>
                  )}
                />
              </Stack>

              <Stack direction="row" spacing={3} flexWrap="wrap">
                <Controller
                  name="modality"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      label={uiPT.register.modality}
                      error={!!errors.modality}
                      helperText={errors.modality?.message}
                      required
                      sx={{
                        flex: 1,
                        minWidth: 250,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          "&:hover fieldset": {
                            borderColor: "#667eea",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#667eea",
                          },
                        },
                      }}
                    >
                      <MenuItem value="bike">{uiPT.register.bike}</MenuItem>
                      <MenuItem value="motorcycle">
                        {uiPT.register.motorcycle}
                      </MenuItem>
                    </TextField>
                  )}
                />
                {!isReferred && (
                      <Controller
                        name="phoneNumber"
                        control={control}
                        render={({ field }) => (
                          <MuiTelInput
                            {...field}
                            label="Telefone WhatsApp"
                            placeholder="+351 912 345 678"
                            error={!!errors.phoneNumber}
                            helperText={errors.phoneNumber?.message}
                            required
                            defaultCountry="PT"
                            preferredCountries={['PT', 'BR', 'US', 'GB', 'FR', 'ES', 'DE', 'IT']}
                            sx={{
                              flex: 1,
                              minWidth: 250,
                              "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                                "&:hover fieldset": {
                                  borderColor: "#667eea",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "#667eea",
                                },
                              },
                            }}
                          />
                        )}
                      />
                )}
              </Stack>

              <Stack direction="row" spacing={3} flexWrap="wrap">
                <Controller
                  name="IBAN"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={uiPT.register.IBAN}
                      placeholder="PT50 1234 5678 9012 3456 7890 1"
                      error={!!errors.IBAN}
                      helperText={errors.IBAN?.message}
                      required
                      onChange={(e) => {
                        const formatted = formatIBAN(e.target.value);
                        field.onChange(formatted);
                      }}
                      sx={{
                        flex: 1,
                        minWidth: 250,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          "&:hover fieldset": {
                            borderColor: "#667eea",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#667eea",
                          },
                        },
                      }}
                    />
                  )}
                />
                <Controller
                  name="identityType"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      label="Tipo de Identidade"
                      error={!!errors.identityType}
                      helperText={errors.identityType?.message}
                      required
                      sx={{
                        flex: 1,
                        minWidth: 250,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          "&:hover fieldset": {
                            borderColor: "#667eea",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#667eea",
                          },
                        },
                      }}
                    >
                      <MenuItem value="passport">Passaporte</MenuItem>
                      <MenuItem value="cc">Cartão de cidadão (CC)</MenuItem>
                      <MenuItem value="trc">Autorização de residência (TRC)</MenuItem>
                      <MenuItem value="cplp">CPLP</MenuItem>
                    </TextField>
                  )}
                />
              </Stack>

              <Stack direction="row" spacing={3} flexWrap="wrap">
                <Controller
                  name="city"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={uiPT.register.city}
                      error={!!errors.city}
                      helperText={errors.city?.message}
                      required
                      sx={{
                        flex: 1,
                        minWidth: 250,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          "&:hover fieldset": {
                            borderColor: "#667eea",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#667eea",
                          },
                        },
                      }}
                    />
                  )}
                />
              </Stack>

              <Stack direction="row" spacing={3} flexWrap="wrap">
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      sx={{
                        flex: 1,
                        minWidth: 250,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          "&:hover fieldset": {
                            borderColor: "#667eea",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#667eea",
                          },
                        },
                      }}
                    >
                      <InputLabel>Status</InputLabel>
                      <Select {...field} label="Status" error={!!errors.status}>
                        {Object.keys(statusTextPT).map((status) => (
                          <MenuItem key={status} value={status}>
                            {status}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
                <Box flex={1} />
              </Stack>

              {/* Referred Client Checkbox */}
              <Box>
                <Controller
                  name="isReferred"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          name="isReferred"
                          color="primary"
                        />
                      }
                      label={uiPT.register.isReferred}
                      sx={{
                        "& .MuiFormControlLabel-label": {
                          fontSize: "0.9rem",
                          color: "#2c3e50",
                          fontWeight: 600,
                        },
                      }}
                    />
                  )}
                />
              </Box>

              {/* Partner Selection (if referred) */}
              {isReferred && (
                <Box>
                  <Typography
                    variant="h6"
                    sx={{ mb: 2, color: "#2c3e50", fontWeight: 600 }}
                  >
                    Parceiro de Referência
                  </Typography>
                  <Controller
                    name="partnerId"
                    control={control}
                    render={({ field }) => (
                      <FormControl
                        sx={{
                          flex: 1,
                          minWidth: 250,
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            "&:hover fieldset": {
                              borderColor: "#667eea",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#667eea",
                            },
                          },
                        }}
                      >
                        <InputLabel>Parceiro</InputLabel>
                        <Select
                          {...field}
                          label="Parceiro"
                          error={!!errors.partnerId}
                          disabled={partnersLoading}
                        >
                          {partnersLoading ? (
                            <MenuItem value="">Carregando...</MenuItem>
                          ) : partners.length === 0 ? (
                            <MenuItem value="">Nenhum parceiro disponível</MenuItem>
                          ) : (
                            partners.map((partner) => (
                              <MenuItem key={partner._id} value={partner._id}>
                                {partner.name}
                              </MenuItem>
                            ))
                          )}
                        </Select>
                        {errors.partnerId && (
                          <Typography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>
                            {errors.partnerId.message}
                          </Typography>
                        )}
                      </FormControl>
                    )}
                  />
                </Box>
              )}

              {/* ID Card Type Selection */}
              <Box>
                <Typography
                  variant="h6"
                  sx={{ mb: 2, color: "#2c3e50", fontWeight: 600 }}
                >
                  Tipo de Documento de Identidade
                </Typography>
                <Controller
                  name="idCardType"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      component="fieldset"
                      error={!!errors.idCardType}
                    >
                      <FormLabel
                        component="legend"
                        sx={{ color: "#2c3e50", fontWeight: 600, mb: 1 }}
                      >
                        Escolha o formato do documento:
                      </FormLabel>
                      <RadioGroup
                        {...field}
                        row
                        sx={{
                          "& .MuiFormControlLabel-root": {
                            marginRight: 3,
                          },
                          "& .MuiRadio-root": {
                            color: "#667eea",
                            "&.Mui-checked": {
                              color: "#667eea",
                            },
                          },
                        }}
                      >
                        <FormControlLabel
                          value="image"
                          control={<Radio />}
                          label="Imagens (Frente e Verso)"
                        />
                        <FormControlLabel
                          value="pdf"
                          control={<Radio />}
                          label="PDF"
                        />
                      </RadioGroup>
                      {errors.idCardType && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ mt: 1, display: "block" }}
                        >
                          {errors.idCardType.message}
                        </Typography>
                      )}
                    </FormControl>
                  )}
                />
              </Box>

              {/* Document Upload Section */}
              <Box>
                <Typography
                  variant="h6"
                  sx={{ mb: 2, color: "#2c3e50", fontWeight: 600 }}
                >
                  Documentos Necessários
                </Typography>
              </Box>

              {/* ID Card Documents */}
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#2c3e50' }}>
                  Documento de Identidade
                </Typography>
                <Stack spacing={2}>
                  {idCardType === "image" ? (
                    <>
                      <Card variant="outlined" sx={{ p: 3 }}>
                        <Stack spacing={2}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {uiPT.register.idCardFront}
                            </Typography>
                            <Chip label="Obrigatório" color="error" size="small" />
                          </Box>
                          <Button
                            component="label"
                            variant="outlined"
                            startIcon={<CloudUploadOutlined />}
                            fullWidth
                            sx={{ justifyContent: 'center' }}
                          >
                            {uiPT.resubmit.selectFile}
                            <input
                              type="file"
                              accept="image/*"
                              hidden
                              onChange={handleFileChange("idCardFront")}
                              required={idCardType === "image"}
                            />
                          </Button>
                          {uploadedFiles.idCardFront && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: 'success.lighter', borderRadius: 1 }}>
                              <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                              <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600, flexGrow: 1 }}>
                                {uploadedFiles.idCardFront}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => removeFile("idCardFront")}
                                sx={{ color: 'error.main' }}
                              >
                                <Cancel sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Box>
                          )}
                          {errors.idCardFront && (
                            <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                              {errors.idCardFront.message}
                            </Typography>
                          )}
                        </Stack>
                      </Card>

                      <Card variant="outlined" sx={{ p: 3 }}>
                        <Stack spacing={2}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {uiPT.register.idCardBack}
                            </Typography>
                            <Chip label="Obrigatório" color="error" size="small" />
                          </Box>
                          <Button
                            component="label"
                            variant="outlined"
                            startIcon={<CloudUploadOutlined />}
                            fullWidth
                            sx={{ justifyContent: 'center' }}
                          >
                            {uiPT.resubmit.selectFile}
                            <input
                              type="file"
                              accept="image/*"
                              hidden
                              onChange={handleFileChange("idCardBack")}
                              required={idCardType === "image"}
                            />
                          </Button>
                          {uploadedFiles.idCardBack && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: 'success.lighter', borderRadius: 1 }}>
                              <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                              <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600, flexGrow: 1 }}>
                                {uploadedFiles.idCardBack}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => removeFile("idCardBack")}
                                sx={{ color: 'error.main' }}
                              >
                                <Cancel sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Box>
                          )}
                          {errors.idCardBack && (
                            <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                              {errors.idCardBack.message}
                            </Typography>
                          )}
                        </Stack>
                      </Card>
                    </>
                  ) : (
                    <Card variant="outlined" sx={{ p: 3 }}>
                      <Stack spacing={2}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {uiPT.register.idCardPdf}
                          </Typography>
                          <Chip label="Obrigatório" color="error" size="small" />
                        </Box>
                        <Button
                          component="label"
                          variant="outlined"
                          startIcon={<CloudUploadOutlined />}
                          fullWidth
                          sx={{ justifyContent: 'center' }}
                        >
                          {uiPT.resubmit.selectFile}
                          <input
                            type="file"
                            accept="application/pdf"
                            hidden
                            onChange={handleFileChange("idCardPdf")}
                            required={idCardType === "pdf"}
                          />
                        </Button>
                        {uploadedFiles.idCardPdf && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: 'success.lighter', borderRadius: 1 }}>
                            <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                            <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600, flexGrow: 1 }}>
                              {uploadedFiles.idCardPdf}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => removeFile("idCardPdf")}
                              sx={{ color: 'error.main' }}
                            >
                              <Cancel sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Box>
                        )}
                        {errors.idCardPdf && (
                          <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                            {errors.idCardPdf.message}
                          </Typography>
                        )}
                      </Stack>
                    </Card>
                  )}
                </Stack>
              </Box>

              {/* Other Documents */}
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#2c3e50' }}>
                  Outros Documentos
                </Typography>
                <Stack spacing={2}>
                  <Card variant="outlined" sx={{ p: 3 }}>
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {uiPT.register.selfie}
                        </Typography>
                        <Chip label="Obrigatório" color="error" size="small" />
                      </Box>
                      <Button
                        component="label"
                        variant="outlined"
                        startIcon={<CloudUploadOutlined />}
                        fullWidth
                        sx={{ justifyContent: 'center' }}
                      >
                        {uiPT.resubmit.selectFile}
                        <input
                          type="file"
                          accept="image/*"
                          hidden
                          onChange={handleFileChange("selfie")}
                          required
                        />
                      </Button>
                      {uploadedFiles.selfie && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: 'success.lighter', borderRadius: 1 }}>
                          <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                          <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600, flexGrow: 1 }}>
                            {uploadedFiles.selfie}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => removeFile("selfie")}
                            sx={{ color: 'error.main' }}
                          >
                            <Cancel sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Box>
                      )}
                      {errors.selfie && (
                        <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                          {errors.selfie.message}
                        </Typography>
                      )}
                    </Stack>
                  </Card>

                  <Card variant="outlined" sx={{ p: 3 }}>
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {uiPT.register.modalityPhoto}
                        </Typography>
                        <Chip label="Obrigatório" color="error" size="small" />
                      </Box>
                      <Button
                        component="label"
                        variant="outlined"
                        startIcon={<CloudUploadOutlined />}
                        fullWidth
                        sx={{ justifyContent: 'center' }}
                      >
                        {uiPT.resubmit.selectFile}
                        <input
                          type="file"
                          accept="image/*"
                          hidden
                          onChange={handleFileChange("modalityPhoto")}
                          required
                        />
                      </Button>
                      {uploadedFiles.modalityPhoto && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: 'success.lighter', borderRadius: 1 }}>
                          <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                          <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600, flexGrow: 1 }}>
                            {uploadedFiles.modalityPhoto}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => removeFile("modalityPhoto")}
                            sx={{ color: 'error.main' }}
                          >
                            <Cancel sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Box>
                      )}
                      {errors.modalityPhoto && (
                        <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                          {errors.modalityPhoto.message}
                        </Typography>
                      )}
                    </Stack>
                  </Card>

                  {modality === 'motorcycle' && (
                    <Card variant="outlined" sx={{ p: 3 }}>
                      <Stack spacing={2}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {uiPT.register.driverLicense}
                          </Typography>
                          <Chip label="Obrigatório" color="error" size="small" />
                        </Box>
                        <Button
                          component="label"
                          variant="outlined"
                          startIcon={<CloudUploadOutlined />}
                          fullWidth
                          sx={{ justifyContent: 'center' }}
                        >
                          {uiPT.resubmit.selectFile}
                          <input
                            type="file"
                            accept="image/*"
                            hidden
                            onChange={handleFileChange("driverLicense")}
                            required={modality === 'motorcycle'}
                          />
                        </Button>
                        {uploadedFiles.driverLicense && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: 'success.lighter', borderRadius: 1 }}>
                            <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                            <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600, flexGrow: 1 }}>
                              {uploadedFiles.driverLicense}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => removeFile("driverLicense")}
                              sx={{ color: 'error.main' }}
                            >
                              <Cancel sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Box>
                        )}
                        {errors.driverLicense && (
                          <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                            {errors.driverLicense.message}
                          </Typography>
                        )}
                      </Stack>
                    </Card>
                  )}

                  {/* CAE Document */}
                  <Card variant="outlined" sx={{ p: 3 }}>
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          Código de Atividade Económica (CAE)
                        </Typography>
                        <Chip label="Obrigatório" color="error" size="small" />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Documento PDF do Código de Atividade Económica
                      </Typography>
                      <Button
                        component="label"
                        variant="outlined"
                        startIcon={<CloudUploadOutlined />}
                        fullWidth
                        sx={{ justifyContent: 'center' }}
                      >
                        {uiPT.resubmit.selectFile}
                        <input
                          type="file"
                          accept="application/pdf"
                          hidden
                          onChange={handleFileChange("caeDocument")}
                          required
                        />
                      </Button>
                      {uploadedFiles.caeDocument && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: 'success.lighter', borderRadius: 1 }}>
                          <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                          <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600, flexGrow: 1 }}>
                            {uploadedFiles.caeDocument}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => removeFile("caeDocument")}
                            sx={{ color: 'error.main' }}
                          >
                            <Cancel sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Box>
                      )}
                      {errors.caeDocument && (
                        <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                          {errors.caeDocument.message}
                        </Typography>
                      )}
                    </Stack>
                  </Card>

                  {/* Tax Clearance Document */}
                  <Card variant="outlined" sx={{ p: 3 }}>
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          Certidão de Não Dívida - Autoridade Tributária
                        </Typography>
                        <Chip label="Obrigatório" color="error" size="small" />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Documento PDF da Certidão de Não Dívida da Autoridade Tributária
                      </Typography>
                      <Button
                        component="label"
                        variant="outlined"
                        startIcon={<CloudUploadOutlined />}
                        fullWidth
                        sx={{ justifyContent: 'center' }}
                      >
                        {uiPT.resubmit.selectFile}
                        <input
                          type="file"
                          accept="application/pdf"
                          hidden
                          onChange={handleFileChange("taxClearanceDocument")}
                          required
                        />
                      </Button>
                      {uploadedFiles.taxClearanceDocument && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: 'success.lighter', borderRadius: 1 }}>
                          <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                          <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600, flexGrow: 1 }}>
                            {uploadedFiles.taxClearanceDocument}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => removeFile("taxClearanceDocument")}
                            sx={{ color: 'error.main' }}
                          >
                            <Cancel sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Box>
                      )}
                      {errors.taxClearanceDocument && (
                        <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                          {errors.taxClearanceDocument.message}
                        </Typography>
                      )}
                    </Stack>
                  </Card>
                </Stack>
              </Box>
            </Stack>

            {error && (
              <Alert
                severity="error"
                sx={{
                  mt: 3,
                  borderRadius: 2,
                  "& .MuiAlert-icon": {
                    color: "#d32f2f",
                  },
                }}
              >
                {error}
              </Alert>
            )}
            {success && (
              <Alert
                severity="success"
                sx={{
                  mt: 3,
                  borderRadius: 2,
                  "& .MuiAlert-icon": {
                    color: "#2e7d32",
                  },
                }}
              >
                {success} <strong>{trackingCode}</strong>
              </Alert>
            )}

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{
                mt: 3,
                py: 1.5,
                textTransform: "none",
                fontSize: "1.1rem",
                fontWeight: 600,
              }}
            >
              {loading ? "Criando..." : "Criar Cliente"}
            </Button>
          </form>
        </Stack>
      </Card>
    </Stack>
  </Container>
</Box>
);
}
